import { Box, Button, CircularProgress, TextField, Typography } from "@mui/material";
import LoginSideImage from '../../images/login/login-side-forgot.png';
import OTPInput from "react-otp-input";
import { useContext, useState } from "react";
import { useNavigate } from "react-router-dom";
import AuthContext from "../../context/AuthContext";
import Fetch from "../../services/Fetch";
import SnackbarAlert from "../../components/SnackBar";
import useSnackBar from "../../hooks/UseSnackBar";
import { useWaits } from "../../hooks/UseWait";
import { useConstants } from "../../hooks/UseConstants";
import TeacherAuthLayout from "../../components/layout/TeacherAuthLayout";

function ForgotPassword() {
    const { host, language } = useConstants();
    const isAr = language === "ar";
    const navigate = useNavigate();
    const { wait } = useContext(AuthContext);
    const { openSnackBar, type, message, setSnackBar, setOpenSnackBar } = useSnackBar();
    const { sendWait, setSendWait } = useWaits();
    const [step, setStep] = useState(1);
    const [email, setEmail] = useState('');
    const [code, setCode] = useState('');
    const [password, setPassword] = useState('');
    const [passwordConfirmation, setPasswordConfirmation] = useState('');

    const forgotPassword = async () => {
        if (!email.trim()) {
            setSnackBar('error', isAr ? 'أدخل البريد الإلكتروني' : 'Enter your email');
            return;
        }

        setSendWait(true);
        const formData = new FormData();
        formData.append('email', email.trim());
        formData.append('account_role', 'teacher');

        const result = await Fetch(`${host}/forget-password`, 'POST', formData);

        if (result.status === 200) {
            setSnackBar('success', isAr ? 'تم إرسال رمز التحقق إلى بريدك' : 'Verification code sent to your email');
            setStep(2);
        } else if (result.status === 422) {
            setSnackBar('error', result.data?.errors?.[0] || (isAr ? 'تعذر إرسال الرمز' : 'Unable to send the code'));
        } else {
            setSnackBar('error', isAr ? 'تعذر إرسال الرمز' : 'Unable to send the code');
        }

        setSendWait(false);
    };

    const resetPassword = async () => {
        if (code.length < 6) {
            setSnackBar('error', isAr ? 'أدخل رمز التحقق المكوّن من 6 أرقام' : 'Enter the 6-digit verification code');
            return;
        }
        if (password.length < 6) {
            setSnackBar('error', isAr ? 'كلمة المرور يجب أن تكون 6 أحرف على الأقل' : 'Password must be at least 6 characters');
            return;
        }
        if (password !== passwordConfirmation) {
            setSnackBar('error', isAr ? 'كلمتا المرور غير متطابقتين' : 'Passwords do not match');
            return;
        }

        setSendWait(true);
        const formData = new FormData();
        formData.append('email', email.trim());
        formData.append('account_role', 'teacher');
        formData.append('code', code);
        formData.append('password', password);
        formData.append('password_confirmation', passwordConfirmation);

        const result = await Fetch(`${host}/forget-password/check-code`, 'POST', formData);

        if (result.status === 200) {
            setSnackBar('success', isAr ? 'تم تحديث كلمة المرور بنجاح' : 'Password updated successfully');
            navigate('/login');
        } else if (result.status === 422) {
            setSnackBar('error', result.data?.errors?.[0] || (isAr ? 'تعذر تحديث كلمة المرور' : 'Unable to update password'));
        } else {
            setSnackBar('error', isAr ? 'تعذر تحديث كلمة المرور' : 'Unable to update password');
        }

        setSendWait(false);
    };

    return (
        <>
            {wait ? (
                <Box className="w-full h-screen relative flex justify-center items-center">
                    <CircularProgress size={70} />
                </Box>
            ) : (
                <TeacherAuthLayout
                    isAr={isAr}
                    title={step === 1 ? (isAr ? 'استعادة كلمة المرور' : 'Forgot password') : (isAr ? 'تعيين كلمة مرور جديدة' : 'Reset password')}
                    subtitle={step === 1
                        ? (isAr ? 'أدخل بريدك الإلكتروني وسنرسل لك رمز تحقق لإعادة ضبط كلمة المرور.' : 'Enter your email and we will send a verification code to reset your password.')
                        : (isAr ? 'أدخل الرمز المرسل إلى بريدك ثم اختر كلمة مرور جديدة.' : 'Enter the code sent to your email, then choose a new password.')}
                    sideTitle={isAr ? 'استعد الوصول إلى حسابك بسرعة' : 'Get back into your account quickly'}
                    sideSubtitle={isAr ? 'خطوتان واضحتان: إرسال الرمز، ثم تعيين كلمة مرور جديدة بشكل آمن.' : 'A simple two-step flow: send the code, then securely set a new password.'}
                    sideImage={LoginSideImage}
                    footer={
                        <Typography
                            variant="body2"
                            sx={{ color: "text.secondary", cursor: "pointer", fontWeight: 700 }}
                            onClick={() => navigate('/login')}
                        >
                            {isAr ? 'العودة إلى تسجيل الدخول' : 'Back to sign in'}
                        </Typography>
                    }
                >
                    {step === 1 ? (
                        <Box sx={{ display: "flex", flexDirection: "column", gap: 2.25 }}>
                            <TextField
                                onChange={(e) => setEmail(e.target.value)}
                                value={email}
                                label={isAr ? "البريد الإلكتروني" : "Email"}
                                type="email"
                                fullWidth
                            />
                            <Button
                                variant="contained"
                                size="large"
                                onClick={forgotPassword}
                                disabled={sendWait}
                                sx={{
                                    minHeight: 52,
                                    borderRadius: 3,
                                    bgcolor: "#0D358C",
                                    fontWeight: 800,
                                    boxShadow: "0 12px 30px rgba(13,53,140,0.25)",
                                    "&:hover": { bgcolor: "#1d4ed8" },
                                }}
                            >
                                {sendWait ? <CircularProgress size={22} color="inherit" /> : (isAr ? 'إرسال الرمز' : 'Send code')}
                            </Button>
                        </Box>
                    ) : (
                        <Box sx={{ display: "flex", flexDirection: "column", gap: 2.25 }}>
                            <Typography variant="body2" sx={{ color: "text.secondary", textAlign: "center" }}>
                                {isAr ? `أدخل الرمز المرسل إلى ${email}` : `Enter the code sent to ${email}`}
                            </Typography>
                            <Box sx={{ display: "flex", justifyContent: "center", direction: "ltr" }}>
                                <OTPInput
                                    value={code}
                                    onChange={setCode}
                                    numInputs={6}
                                    inputStyle={{
                                        width: '3rem',
                                        height: '3rem',
                                        margin: '0 0.3rem',
                                        fontSize: '1.2rem',
                                        borderRadius: '12px',
                                        border: '1px solid #cbd5e1',
                                        background: 'transparent',
                                    }}
                                    renderInput={(props) => <input {...props} />}
                                />
                            </Box>
                            <TextField
                                type="password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                label={isAr ? "كلمة المرور الجديدة" : "New password"}
                                fullWidth
                            />
                            <TextField
                                type="password"
                                value={passwordConfirmation}
                                onChange={(e) => setPasswordConfirmation(e.target.value)}
                                label={isAr ? "تأكيد كلمة المرور" : "Confirm password"}
                                fullWidth
                            />
                            <Button
                                variant="contained"
                                size="large"
                                onClick={resetPassword}
                                disabled={sendWait}
                                sx={{
                                    minHeight: 52,
                                    borderRadius: 3,
                                    bgcolor: "#0D358C",
                                    fontWeight: 800,
                                    boxShadow: "0 12px 30px rgba(13,53,140,0.25)",
                                    "&:hover": { bgcolor: "#1d4ed8" },
                                }}
                            >
                                {sendWait ? <CircularProgress size={22} color="inherit" /> : (isAr ? 'تحديث كلمة المرور' : 'Update password')}
                            </Button>
                        </Box>
                    )}
                </TeacherAuthLayout>
            )}
            <SnackbarAlert open={openSnackBar} message={message} severity={type} onClose={() => setOpenSnackBar(false)} />
        </>
    );
}

export default ForgotPassword;
